import React, { useEffect, useState } from "react";
import { getContent } from "../lib/api";
import { SEO } from "../components/SEO";
import { BannerSlider } from "../components/home/BannerSlider";
import { Ecosystem } from "../components/home/Ecosystem";
import { WhyUs } from "../components/home/WhyUs";
import { FeaturedProjects } from "../components/home/FeaturedProjects";
import { RegionsPreview } from "../components/home/RegionsPreview";
import { PackagesPreview } from "../components/home/PackagesPreview";
import { Process } from "../components/home/Process";
import { FinancialBenefits, CTASection } from "../components/home/FinancialBenefits";
import { Testimonials } from "../components/home/Testimonials";
import { SectionShell } from "../components/editor/SectionShell";
import { EditorBar } from "../components/editor/EditorBar";
import { useLayout } from "../context/LayoutContext";
import { useLang } from "../context/LanguageContext";

const CACHE_KEY = "sf_home_cache_v1";
const readCache = () => {
  try {
    const raw = JSON.parse(localStorage.getItem(CACHE_KEY));
    return raw && Array.isArray(raw.projects) && raw.projects.length ? raw : null;
  } catch {
    return null;
  }
};

export default function Home() {
  const { t } = useLang();
  const { sections } = useLayout();
  const cached = React.useRef(readCache()).current;
  const [data, setData] = useState(cached || { projects: [], regions: [], packages: [], testimonials: [] });
  const [loaded, setLoaded] = useState(!!cached);

  useEffect(() => {
    // Banner only needs projects — fetch it on its own so the hero paints as early as possible.
    getContent("projects")
      .then((projects) => setData((d) => ({ ...d, projects })))
      .catch(() => {})
      .finally(() => setLoaded(true));

    Promise.all([getContent("regions"), getContent("packages"), getContent("testimonials")])
      .then(([regions, packages, testimonials]) => setData((d) => ({ ...d, regions, packages, testimonials })))
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!data.projects.length || !data.packages.length) return;
    try {
      localStorage.setItem(CACHE_KEY, JSON.stringify(data));
    } catch {}
  }, [data]);

  const schema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: "Smart Farmland A.Ş.",
    description: t.seo.home.description,
    slogan: t.slogan,
  };

  const blocks = {
    banner: <BannerSlider projects={data.projects} ready={loaded} />,
    featured: <FeaturedProjects projects={data.projects} />,
    packages: <PackagesPreview packages={data.packages} />,
    ecosystem: <Ecosystem />,
    why: <WhyUs />,
    regions: <RegionsPreview regions={data.regions} projects={data.projects} />,
    process: <Process />,
    financial: <FinancialBenefits />,
    testimonials: <Testimonials testimonials={data.testimonials} />,
    cta: <CTASection />,
  };

  return (
    <>
      <SEO pageKey="home" title={t.seo.home.title} description={t.seo.home.description} schema={schema} />
      {sections.map((section) => (
        <SectionShell key={section.key} section={section}>
          {blocks[section.key]}
        </SectionShell>
      ))}
      <EditorBar />
    </>
  );
}
